// A borda entre o documento autoral e o blob que a API grava.
//
// TODA esquisitice do formato morre aqui (princípio do épico): row-major,
// coordenadas 1-indexadas como STRING, intervalos "início-fim", solução minúscula
// no legado e o campo `texto` com as soluções juntadas por '*'. O resto do módulo
// só enxerga o `WordsearchDoc`.

import type { ICell, IHuntWordData } from '../../../../../games/HuntWords/huntWordInterface';
import { cellIndex, normalizeSolution, type WordEntry, type WordsearchDoc } from './doc';

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/** Teto de área da grade. Blob malformado com dimensão enorme não pode derrubar
 *  o editor: `new Array(width * height)` estoura RangeError antes de qualquer
 *  validação de negócio. 10.000 células é bem acima de qualquer caça-palavras real. */
const MAX_CELLS = 10_000;

/**
 * "2-4" → { from: 2, to: 4 }. "3" (sem hífen) vira { from: 3, to: 3 }.
 * Só aceita dígitos (com hífen único de separador); qualquer outra coisa
 * (várias partes, hífen de negativo, string vazia, lixo) devolve `null`,
 * que o chamador trata descartando a palavra.
 */
function parseRange(raw: unknown): { from: number; to: number } | null {
  if (typeof raw !== 'string' || !/^\d+(-\d+)?$/.test(raw)) return null;
  const parts = raw.split('-');
  const from = Number(parts[0]);
  const to = parts.length > 1 ? Number(parts[1]) : from;
  return { from, to };
}

export function docToBlob(doc: WordsearchDoc): { game: IHuntWordData; texto: string } {
  const cell: ICell[] = [];
  for (let y = 1; y <= doc.height; y++) {
    for (let x = 1; x <= doc.width; x++) {
      cell.push({
        attributes: {
          x: String(x),
          y: String(y),
          solution: doc.letters[cellIndex(x, y, doc.width)] ?? '',
        },
      });
    }
  }

  // O id da palavra no blob é posicional (1-based), como no legado. O `ref` do doc
  // é do cliente e não trafega.
  //
  // Contrato de case ASSIMÉTRICO no legado (ver gerador da API,
  // generate-wordsearch.ts): célula e `texto` saem em MAIÚSCULA, mas
  // `word.attributes.solution` sai em minúscula — é o que o player
  // (HuntWords/index.tsx) compara com `el.toLowerCase()` para achar o índice
  // da palavra no `texto`. Maiúscula aqui quebra esse `findIndex` em silêncio.
  const word = doc.words.map((w, i) => ({
    attributes: {
      id: String(i + 1),
      x: `${w.x1}-${w.x2}`,
      y: `${w.y1}-${w.y2}`,
      solution: w.solution.toLowerCase(),
    },
  }));

  const clue = doc.words.map((w, i) => ({
    text: w.clue,
    attributes: { word: String(i + 1) },
  }));

  const game: IHuntWordData = {
    'crossword-compiler': {
      'rectangular-puzzle': {
        attributes: { xmlns: 'http://crossword.info/xml/rectangular-puzzle', alphabet: '' },
        'word-search': {
          grid: { cell, attributes: { width: String(doc.width), height: String(doc.height) } },
          word,
          clues: { clue },
        },
      },
    },
  };

  return { game, texto: doc.words.map((w) => w.solution).join('*') };
}

export function blobToDoc(
  res: { game: unknown; texto: string | null },
  titulo: string,
): WordsearchDoc {
  const empty: WordsearchDoc = { titulo, width: 0, height: 0, letters: [], words: [] };

  if (!isRecord(res.game)) return empty;
  const compiler = res.game['crossword-compiler'];
  if (!isRecord(compiler)) return empty;
  const puzzle = compiler['rectangular-puzzle'];
  if (!isRecord(puzzle)) return empty;
  const ws = puzzle['word-search'];
  if (!isRecord(ws)) return empty;

  const grid = isRecord(ws.grid) ? ws.grid : null;
  if (!grid) return empty;
  const gridAttrs = isRecord(grid.attributes) ? grid.attributes : {};
  // `Number.isFinite` sozinho aceita '-1' e '1.5', que viram um `new
  // Array(width * height)` de comprimento inválido e estouram `RangeError`.
  // Dimensão de grade só faz sentido como inteiro não-negativo.
  const validDim = (v: unknown): v is number =>
    typeof v === 'number' && Number.isInteger(v) && v >= 0;
  const width = Number(gridAttrs.width);
  const height = Number(gridAttrs.height);
  if (!validDim(width) || !validDim(height)) return empty;
  if (width * height > MAX_CELLS) return empty;

  const letters: string[] = new Array(width * height).fill('');
  const cells = Array.isArray(grid.cell) ? grid.cell : [];
  for (const c of cells) {
    if (!isRecord(c) || !isRecord(c.attributes)) continue;
    const x = Number(c.attributes.x);
    const y = Number(c.attributes.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    const idx = cellIndex(x, y, width);
    if (idx < 0 || idx >= letters.length) continue;
    letters[idx] = normalizeSolution(String(c.attributes.solution ?? ''));
  }

  // dica por id de palavra
  const clueByWord = new Map<string, string>();
  const cluesHolder = isRecord(ws.clues) ? ws.clues : null;
  const clueList = cluesHolder && Array.isArray(cluesHolder.clue) ? cluesHolder.clue : [];
  for (const c of clueList) {
    if (!isRecord(c) || !isRecord(c.attributes)) continue;
    clueByWord.set(String(c.attributes.word), typeof c.text === 'string' ? c.text : '');
  }

  const words: WordEntry[] = [];
  const usedIds = new Set<string>();
  const wordList = Array.isArray(ws.word) ? ws.word : [];
  wordList.forEach((w, i) => {
    if (!isRecord(w) || !isRecord(w.attributes)) return;
    const rx = parseRange(w.attributes.x);
    const ry = parseRange(w.attributes.y);
    if (!rx || !ry) return;
    const id = String(w.attributes.id ?? i + 1);
    // Id duplicado no blob (dado corrompido) geraria dois refs "w{id}"
    // colidentes — qualquer `words.find(w => w.ref === ref)` (seleção, edição,
    // key do React) só enxergaria o primeiro. Decora com o índice quando o id
    // já foi visto.
    const ref = usedIds.has(id) ? `w${id}-${i}` : `w${id}`;
    usedIds.add(id);
    words.push({
      // Prefixo 'w' identifica refs derivados de um jogo CARREGADO do servidor,
      // nunca colidindo com os refs "n{n}" de palavras novas criadas no cliente
      // (ver CrosswordUi.nextRef em modules/crossword/ui.ts).
      ref,
      solution: normalizeSolution(String(w.attributes.solution ?? '')),
      clue: clueByWord.get(id) ?? '',
      x1: rx.from,
      y1: ry.from,
      x2: rx.to,
      y2: ry.to,
    });
  });

  return { titulo, width, height, letters, words };
}
